import { PlusIcon, SearchIcon, UsersIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  CUSTOMER_PAGE_SIZE,
  useCustomerFilters,
  useCustomerList,
} from "@/features/customers/use-customer-list"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import type { Customer } from "@/lib/endpoints"
import { routes } from "@/routes/paths"

/**
 * The customer book (§7, §11.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SEARCH IS SERVER-SIDE, over name, phone AND GSTIN. `CustomerListQuerySchema`
 * declares `q` and the handler matches all three, which is what the owner
 * actually needs: he remembers "Lupin", his son has a GSTIN off an invoice, and
 * a caller is identified by the number that rang. Filtering the fetched page in
 * the browser would look identical on a short list and silently miss everyone
 * from page two onward.
 *
 * ARCHIVED CUSTOMERS ARE SHOWN, MARKED — not hidden. A customer is never
 * deleted, because every invoice ever raised for them still references the row
 * (§4). Hiding them by default would make an old bill's buyer look as though
 * they had vanished from the system.
 *
 * NO DELETE BUTTON ANYWHERE, for the same reason. The only withdrawal is the
 * "still working with them" switch on the editor, which sets `is_active`.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function CustomerListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()

  const { filters, setFilters, clearFilters, activeCount } = useCustomerFilters()
  const query = useCustomerList(filters)

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.customers")}</h1>
          <p className="text-muted-foreground text-sm">{t("customer.listSubtitle")}</p>
        </div>
        {can("customer.create") ? (
          <Button nativeButton={false} render={<Link to={routes.customerNew} />}>
            <PlusIcon />
            {t("btn.addNewCustomer")}
          </Button>
        ) : null}
      </header>

      <CustomerFilterBar filters={filters} setFilters={setFilters} />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        /* An empty book and a search that matched nothing are different facts.
         * "No customers yet" on a search for "Lupni" would send the owner
         * looking for a customer he has not lost. */
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <UsersIcon />
            </EmptyMedia>
            <EmptyTitle>
              {activeCount > 0 ? t("customer.noneFound") : t("customer.emptyTitle")}
            </EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("customer.noneFoundHelp") : t("customer.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : can("customer.create") ? (
              <Button nativeButton={false} render={<Link to={routes.customerNew} />}>
                <PlusIcon />
                {t("btn.addNewCustomer")}
              </Button>
            ) : null}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* Phone: one card per customer, the whole row a tap target. */}
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((customer) => (
                <li key={customer.id} className="border-b last:border-b-0">
                  <Link
                    to={routes.customer(customer.id)}
                    className="hover:bg-accent/50 flex min-h-14 flex-col gap-1 p-4 transition-colors"
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{customer.name}</span>
                      <ArchivedMark customer={customer} />
                    </span>
                    <span className="text-muted-foreground text-sm tabular-nums">
                      {customer.gstin ?? t("customer.noGstin")}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {customer.stateName} ({customer.stateCode})
                      {customer.phone === null ? "" : ` · ${customer.phone}`}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          {/* Desktop: the book. */}
          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("lbl.customerName")}</TableHead>
                  <TableHead>{t("lbl.gstin")}</TableHead>
                  <TableHead>{t("lbl.state")}</TableHead>
                  <TableHead>{t("lbl.phone")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((customer) => (
                  // `relative` so the stretched link below resolves against the
                  // ROW and not the page — see InvoiceListScreen for the full note.
                  <TableRow key={customer.id} className="hover:bg-accent/50 relative cursor-pointer">
                    <TableCell className="font-medium">
                      <Link
                        to={routes.customer(customer.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {customer.name}
                      </Link>
                      <ArchivedMark customer={customer} className="ml-2" />
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {customer.gstin ?? (
                        <span className="text-muted-foreground">{t("customer.noGstin")}</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {customer.stateName} ({customer.stateCode})
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {customer.phone ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/** A customer taken out of use. Never deleted — old bills still reference them. */
function ArchivedMark({
  customer,
  className,
}: {
  customer: Customer
  className?: string
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (customer.isActive) return null

  return (
    <Badge variant="secondary" className={`text-xs ${className ?? ""}`}>
      {t("customer.archived")}
    </Badge>
  )
}

/**
 * Search and the in-use filter.
 *
 * THE SEARCH BOX IS DEBOUNCED, and it is a local `useState` mirroring the URL
 * rather than reading it directly. Writing every keystroke straight to the URL
 * fires a request per character and, worse, makes the input laggy on a phone
 * because each change re-renders the whole screen through the router.
 *
 * THE IN-USE FILTER IS NO LONGER A NATIVE `<select>` (2026-08-16, at the
 * owner's request, for visual consistency with the rest of the UI). The reason
 * the native one was chosen is worth keeping: on a phone it opened the
 * operating system's own wheel, which is reliably hittable without the page
 * having to draw a 48px target (§11.1). That target is now drawn by hand, as
 * `` on every item and on the trigger — the rem class rather than a
 * pixel literal so it grows with the large-text preference. DO NOT TIDY IT
 * AWAY. The full version of this note is above `InvoiceFilterBar` in the bill
 * register.
 */
function CustomerFilterBar({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useCustomerFilters>["filters"]
  setFilters: ReturnType<typeof useCustomerFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  /*
   * A STRINGIFIED TRI-STATE: the filter is `boolean | undefined`, so ""/"true"/
   * "false" is the whole vocabulary and the mapping runs both ways. The array is
   * also handed to `<Select items={…}>`, which is how Base UI resolves the
   * trigger's text; without it the trigger would print the raw value and read
   * "true".
   */
  const activeOptions = [
    { value: "", label: t("list.anyStatus") },
    { value: "true", label: t("customer.inUse") },
    { value: "false", label: t("customer.archived") },
  ]

  // Keep the box in step when the URL changes from somewhere else — the back
  // button, or "clear filters" in the empty state.
  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-sm">
        <Label htmlFor="customer-search">{t("customer.searchLabel")}</Label>
        <div className="relative">
          <SearchIcon
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          />
          <Input
            id="customer-search"
            type="search"
            className="pl-9"
            placeholder={t("customer.searchPlaceholder")}
            value={text}
            onChange={(event) => {
              setText(event.target.value)
            }}
          />
        </div>
      </div>

      <div className="flex min-w-40 flex-col gap-1.5">
        <Label htmlFor="customer-active">{t("list.statusFilter")}</Label>
        <Select
          items={activeOptions}
          value={filters.active === undefined ? "" : String(filters.active)}
          onValueChange={(value) => {
            /* "" MEANS NO FILTER, and it must leave as `undefined`: the list
             * schema is `.strict()`, so `?active=` is a 400 rather than a no-op.
             * Base UI hands the string over directly (there is no
             * `event.target`) and types it nullable, so null folds into the same
             * branch. */
            setFilters({ active: value === "" || value == null ? undefined : value === "true" })
          }}
        >
          {/* The id goes on the TRIGGER, which is the button `<Label htmlFor>`
            * above labels — a <button> is a labelable element, so
            * `getByLabelText` still resolves this control. */}
          <SelectTrigger id="customer-active" className="w-full">
            {/* The same string as the "" item: Base UI reads an empty value as
              * NO value, so without this the trigger would show a greyed-out
              * placeholder where the native select showed "All". */}
            <SelectValue placeholder={t("list.anyStatus")} />
          </SelectTrigger>
          {/* `w-auto` because the popup otherwise matches the trigger exactly and
            * clips "Still working with them"; the OS wheel sized itself. */}
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {activeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} className="pl-3">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= CUSTOMER_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - CUSTOMER_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + CUSTOMER_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56" />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}
